export const demoQuestions = [
  {
    id: 'leaf-arrangement',
    technicalTerm: 'Leaf arrangement',
    plainPrompt: 'How are the leaves positioned on the twig?',
    advancedPrompt: 'Record the leaf arrangement.',
    organ: 'Leaf and twig',
    material: ['leaves', 'twigs'],
    difficulty: 'easy',
    illustration: 'arrangement',
    why: 'This broad pattern separates several anonymous profiles early.',
    how: 'Follow one twig and compare two or three leaf attachment points.',
    beginnerHow: 'Start at the tip and trace the twig backwards. Look for pairs at the same level or single leaves at alternating levels.',
    commonMistake: 'Do not compare leaves from separate twigs.',
    examples: 'Opposite means a pair shares one level; alternate means one leaf occurs at each level.',
    demoGain: 1.15,
    states: [
      { id: 'alternate', label: 'One at each level', technical: 'Alternate' },
      { id: 'opposite', label: 'Paired at each level', technical: 'Opposite' },
      { id: 'whorled', label: 'Three or more at a level', technical: 'Whorled' },
    ],
  },
  {
    id: 'leaf-type',
    technicalTerm: 'Leaf type',
    plainPrompt: 'Is each leaf one blade or divided into leaflets?',
    advancedPrompt: 'Record whether the leaf is simple or compound.',
    organ: 'Leaf',
    material: ['leaves'],
    difficulty: 'easy',
    illustration: 'leaf-type',
    why: 'Blade division is a clear second check that separates the demo profiles.',
    how: 'Find the point where the whole leaf joins the twig, then inspect everything beyond it.',
    beginnerHow: 'Look for a bud at the base of the whole leaf. Small divisions beyond that point are leaflets, not separate leaves.',
    commonMistake: 'A deeply cut simple leaf can look like several leaflets.',
    examples: 'A simple leaf has one blade; a compound leaf has separate leaflets on one leaf stalk.',
    demoGain: 1.05,
    states: [
      { id: 'simple', label: 'One blade', technical: 'Simple' },
      { id: 'compound', label: 'Several leaflets', technical: 'Compound' },
    ],
  },
  {
    id: 'leaf-margin',
    technicalTerm: 'Leaf margin',
    plainPrompt: 'What does the outer edge of the leaf look like?',
    advancedPrompt: 'Record the leaf-margin form.',
    organ: 'Leaf edge',
    material: ['leaves'],
    difficulty: 'easy',
    illustration: 'margin',
    why: 'Edge form divides the remaining profiles in a different way from leaf arrangement.',
    how: 'Inspect an undamaged middle section of a mature leaf edge.',
    beginnerHow: 'Ignore tears and insect damage. Compare both sides of more than one leaf.',
    commonMistake: 'Large lobes are not the same as small teeth.',
    examples: 'Entire is smooth; toothed has small repeated points; lobed has large rounded or pointed divisions.',
    demoGain: 1,
    allowVariable: true,
    states: [
      { id: 'entire', label: 'Smooth edge', technical: 'Entire' },
      { id: 'toothed', label: 'Small teeth', technical: 'Toothed' },
      { id: 'lobed', label: 'Large divisions', technical: 'Lobed' },
    ],
  },
  {
    id: 'leaf-lobes',
    technicalTerm: 'Lobe depth',
    plainPrompt: 'Do the large leaf divisions reach far toward the centre?',
    advancedPrompt: 'Record whether the lobes are shallow or deep.',
    organ: 'Leaf blade',
    material: ['leaves'],
    difficulty: 'intermediate',
    illustration: 'lobes',
    why: 'This conditional check refines a previously recorded lobed margin.',
    how: 'Compare the deepest indentation with the distance from edge to midrib.',
    beginnerHow: 'Only answer this when you recorded large lobes. Small teeth do not count.',
    commonMistake: 'Damage or missing tissue can imitate a deep indentation.',
    examples: 'Shallow lobes stay near the edge; deep lobes approach the centre line.',
    demoGain: .72,
    condition: { questionId: 'leaf-margin', stateId: 'lobed' },
    states: [
      { id: 'shallow', label: 'Shallow divisions', technical: 'Shallowly lobed' },
      { id: 'deep', label: 'Deep divisions', technical: 'Deeply lobed' },
    ],
  },
  {
    id: 'leaf-attachment',
    technicalTerm: 'Leaf attachment',
    plainPrompt: 'Does the leaf blade have a visible stalk?',
    advancedPrompt: 'Record petiolate or sessile attachment.',
    organ: 'Leaf base',
    material: ['leaves'],
    difficulty: 'easy',
    illustration: 'attachment',
    why: 'Attachment checks the leaf base rather than repeating an edge feature.',
    how: 'Trace the blade base to the twig and look for a narrow connecting stalk.',
    beginnerHow: 'Hold the twig still and look between the broad blade and the twig.',
    commonMistake: 'A very short stalk can look absent from a distance.',
    examples: 'Petiolate has a stalk; sessile joins without an obvious stalk.',
    demoGain: .9,
    states: [
      { id: 'petiolate', label: 'Visible leaf stalk', technical: 'Petiolate' },
      { id: 'sessile', label: 'No obvious stalk', technical: 'Sessile' },
    ],
  },
  {
    id: 'surface-hairiness',
    technicalTerm: 'Surface hairiness',
    plainPrompt: 'Can you see or feel hairs on the leaf or young twig?',
    advancedPrompt: 'Record visible surface hairs.',
    organ: 'Leaf or young twig',
    material: ['leaves', 'twigs'],
    tool: 'handLens',
    difficulty: 'advanced',
    illustration: 'hairiness',
    why: 'A magnified surface check can separate profiles that share larger features.',
    how: 'Use side lighting and inspect the same controlled surface with a hand lens.',
    beginnerHow: 'Tilt the surface toward the light. Do not guess if you lack a lens or cannot hold it steady.',
    commonMistake: 'Dust, fibres and edge hairs can be mistaken for hairs across the surface.',
    examples: 'Hairless looks even; hairy shows repeated projections attached to the surface.',
    demoGain: .92,
    states: [
      { id: 'hairless', label: 'No visible hairs', technical: 'Glabrous in this demo' },
      { id: 'hairy', label: 'Visible hairs', technical: 'Hairy' },
    ],
  },
  {
    id: 'bud-arrangement',
    technicalTerm: 'Bud arrangement',
    plainPrompt: 'Are the buds paired or staggered along the twig?',
    advancedPrompt: 'Record the bud arrangement.',
    organ: 'Twig and bud',
    material: ['twigs'],
    difficulty: 'intermediate',
    illustration: 'buds',
    why: 'Bud position provides an independent twig check and can expose contradictory observations.',
    how: 'Inspect several nodes on one recent twig section.',
    beginnerHow: 'Look for buds at the same height on opposite sides, or single buds that alternate up the twig.',
    commonMistake: 'Broken or hidden buds can make a pair look single.',
    examples: 'Paired buds share a level; staggered buds occur one at a time.',
    demoGain: 1,
    states: [
      { id: 'alternate', label: 'Staggered', technical: 'Alternate' },
      { id: 'opposite', label: 'Paired', technical: 'Opposite' },
    ],
  },
  {
    id: 'bud-visibility',
    technicalTerm: 'Bud visibility',
    plainPrompt: 'Are the buds easy to see without moving other material?',
    advancedPrompt: 'Record whether buds are exposed or concealed.',
    organ: 'Twig and bud',
    material: ['twigs'],
    difficulty: 'intermediate',
    illustration: 'bud-visibility',
    why: 'Visibility demonstrates a field limitation without assuming that absence means hidden.',
    how: 'Check several intact nodes from more than one viewing angle.',
    beginnerHow: 'If you cannot find a clear node, choose Cannot observe instead of hidden.',
    commonMistake: 'A missing bud is not evidence that buds are normally concealed.',
    examples: 'Exposed buds project clearly; concealed buds remain difficult to see at an intact node.',
    demoGain: .55,
    states: [
      { id: 'visible', label: 'Easy to see', technical: 'Exposed' },
      { id: 'concealed', label: 'Difficult to see', technical: 'Concealed' },
    ],
  },
  {
    id: 'thorn-presence',
    technicalTerm: 'Thorn or spine presence',
    plainPrompt: 'Are there firm sharp structures on the available twig or plant?',
    advancedPrompt: 'Record thorn or spine presence for this specimen.',
    organ: 'Twig or whole plant',
    material: ['twigs', 'habit'],
    difficulty: 'easy',
    illustration: 'thorns',
    why: 'A presence/absence check demonstrates a binary field observation.',
    how: 'Look along intact young and older sections without handling sharp material directly.',
    beginnerHow: 'Observe from a safe angle. If only one damaged point is visible, choose Not sure.',
    commonMistake: 'Broken twig tips can look like deliberate sharp structures.',
    examples: 'Present means a repeated attached sharp structure; absent means none found on representative available material.',
    demoGain: .82,
    states: [
      { id: 'absent', label: 'None observed', technical: 'Absent on available material' },
      { id: 'present', label: 'Sharp structures present', technical: 'Present' },
    ],
  },
  {
    id: 'leaf-apex',
    technicalTerm: 'Leaf apex',
    plainPrompt: 'Is the leaf tip rounded or drawn into a point?',
    advancedPrompt: 'Record the leaf-apex form.',
    organ: 'Leaf tip',
    material: ['leaves'],
    difficulty: 'intermediate',
    illustration: 'apex',
    why: 'Tip shape checks a small feature after broader leaf characters.',
    how: 'Use an intact mature leaf and compare more than one tip.',
    beginnerHow: 'Ignore leaves with a torn or folded tip.',
    commonMistake: 'A tiny terminal tooth can make a rounded tip seem pointed.',
    examples: 'Rounded curves to the end; pointed narrows clearly toward the end.',
    demoGain: .7,
    allowVariable: true,
    states: [
      { id: 'rounded', label: 'Rounded', technical: 'Rounded apex' },
      { id: 'pointed', label: 'Pointed', technical: 'Acute in this demo' },
    ],
  },
  {
    id: 'leaf-length',
    technicalTerm: 'Leaf length',
    plainPrompt: 'About how long is a representative leaf?',
    advancedPrompt: 'Measure leaf length from blade base to apex.',
    organ: 'Leaf',
    material: ['leaves'],
    tool: 'ruler',
    difficulty: 'advanced',
    illustration: 'measurement',
    responseType: 'numeric',
    unit: 'cm',
    min: 1,
    max: 20,
    step: .5,
    why: 'A simple measurement demonstrates numeric input and tool-dependent question selection.',
    how: 'Measure the blade from its base to the tip; exclude the leaf stalk.',
    beginnerHow: 'Lay one typical intact leaf beside the ruler. Do not stretch or include the stalk.',
    commonMistake: 'Including the leaf stalk changes the result.',
    examples: 'Enter a representative value in centimetres; use Variable or mixed if leaves differ greatly.',
    demoGain: .84,
    allowVariable: true,
  },
  {
    id: 'fruit-presence',
    technicalTerm: 'Fruit presence',
    plainPrompt: 'Is fruit visible on the available material?',
    advancedPrompt: 'Record visible fruiting material.',
    organ: 'Reproductive material',
    material: ['fruits'],
    months: [8, 9, 10, 11],
    difficulty: 'easy',
    illustration: 'fruit',
    why: 'This demonstrates how season and material availability affect question priority.',
    how: 'Check attached material; do not infer fruit from objects lying nearby.',
    beginnerHow: 'Only record what is attached or clearly belongs to the observed specimen.',
    commonMistake: 'Nearby fallen material may come from another plant.',
    examples: 'Present means visible attached fruiting material in this demo.',
    demoGain: .48,
    states: [
      { id: 'absent', label: 'No fruit visible', technical: 'Not observed on available material' },
      { id: 'present', label: 'Fruit visible', technical: 'Present' },
    ],
  },
]

export const demoGlossary = demoQuestions.map((question) => ({
  id: question.id,
  term: question.technicalTerm,
  definition: question.examples,
  organ: question.organ,
  advice: question.how,
  related: question.states?.map((state) => state.technical).join(', ') || `Measurement in ${question.unit}`,
  confusion: question.commonMistake,
  illustration: question.illustration,
}))

